/**
 * Custom hooks for OKR approval workflow
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { approvalService } from '../services/approval.service';
import { toast } from 'react-hot-toast';

// Type for API error responses
interface ApiError {
  response?: { data?: { message?: string } };
}

/**
 * Hook to check if approval workflow is enabled
 */
export const useApprovalEnabled = () => {
  return useQuery({
    queryKey: ['approval', 'enabled'],
    queryFn: () => approvalService.checkApprovalEnabled(),
    staleTime: 1000 * 60 * 5, // 5 minutes - config doesn't change often
  });
};

/**
 * Hook to get pending approvals for current user (manager)
 */
export const usePendingApprovals = () => {
  return useQuery({
    queryKey: ['approvals', 'pending'],
    queryFn: () => approvalService.getPendingApprovals(),
    refetchOnWindowFocus: true, // Refresh when user returns to tab
  });
};

/**
 * Hook to submit OKR for approval
 */
export const useSubmitForApproval = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (okrId: string) => approvalService.submitForApproval(okrId),
    onSuccess: (_data, okrId) => {
      // Invalidate OKR queries to show updated status
      queryClient.invalidateQueries({ queryKey: ['okr', okrId] });
      queryClient.invalidateQueries({ queryKey: ['okrs'] });

      // Show success message
      toast.success('OKR submitted for approval');
    },
    onError: (error: ApiError) => {
      // Show error message
      const message = error.response?.data?.message || 'Failed to submit OKR for approval';
      toast.error(message);
    }
  });
};

/**
 * Hook to approve or reject OKR
 */
export const useReviewApproval = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      okrId,
      action,
      comments
    }: {
      okrId: string;
      action: 'approved' | 'rejected';
      comments?: string;
    }) => {
      if (action === 'approved') {
        return approvalService.approveOKR(okrId, comments);
      } else {
        return approvalService.rejectOKR(okrId, comments);
      }
    },
    onSuccess: (_data, variables) => {
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['okr', variables.okrId] });
      queryClient.invalidateQueries({ queryKey: ['okrs'] });
      queryClient.invalidateQueries({ queryKey: ['approvals', 'pending'] });
      queryClient.invalidateQueries({ queryKey: ['approvals', 'history', variables.okrId] });

      // Show success message
      const action = variables.action === 'approved' ? 'approved' : 'rejected';
      toast.success(`OKR ${action} successfully`);
    },
    onError: (error: ApiError) => {
      const message = error.response?.data?.message || 'Failed to review OKR';
      toast.error(message);
    }
  });
};

/**
 * Hook to bulk approve multiple OKRs
 */
export const useBulkApprove = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      okrIds,
      comments
    }: {
      okrIds: string[];
      comments?: string;
    }) => approvalService.bulkApprove(okrIds, comments),
    onSuccess: (results) => {
      // Count successes and failures
      const successCount = results.filter(r => r.success).length;
      const failureCount = results.filter(r => !r.success).length;

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['okrs'] });
      queryClient.invalidateQueries({ queryKey: ['approvals', 'pending'] });

      // Show appropriate message
      if (failureCount === 0) {
        toast.success(`Successfully approved ${successCount} OKRs`);
      } else if (successCount === 0) {
        toast.error(`Failed to approve all ${failureCount} OKRs`);
      } else {
        toast.success(`Approved ${successCount} OKRs, ${failureCount} failed`);
      }
    },
    onError: (error: ApiError) => {
      const message = error.response?.data?.message || 'Failed to bulk approve OKRs';
      toast.error(message);
    }
  });
};

/**
 * Hook to get approval history for an OKR
 */
export const useApprovalHistory = (okrId: string) => {
  return useQuery({
    queryKey: ['approvals', 'history', okrId],
    queryFn: () => approvalService.getApprovalHistory(okrId),
    enabled: !!okrId,
  });
};
